/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Compass, Crosshair, Dumbbell, Sparkles, Lock } from "lucide-react";

const ACTION_META = {
  explore: { label: "Exploration",  icon: Compass,  description: "Repère des Pokémon et trouve des objets. Aucune Poké Ball consommée." },
  capture: { label: "Capture",      icon: Crosshair, description: "Tente de capturer un Pokémon. Consomme 1 Poké Ball en cas de réussite." },
  train:   { label: "Entraînement", icon: Dumbbell,  description: "Gagne de l'XP. Routes avancées = plus d'XP mais risque d'échec si l'équipe est trop faible." },
};

const DIFFICULTY_LABEL = (d) =>
  d <= 2 ? "Facile" : d <= 5 ? "Moyenne" : d <= 8 ? "Difficile" : "Élite";

export default function ForceActionModal({ trainerId, action, open, onClose, onAfterSubmit }) {
  const [routes, setRoutes] = useState(null);
  const [selected, setSelected] = useState(null);
  const [repeat, setRepeat] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !trainerId) return;
    setSelected(null);
    setRepeat(1);
    setRoutes(null);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/trainers/${trainerId}/routes`);
        if (!cancelled) setRoutes(data);
      } catch (err) {
        if (!cancelled) {
          toast.error(formatApiError(err.response?.data?.detail));
          onClose();
        }
      }
    })();
    return () => { cancelled = true; };
    // Intentionally omit `onClose` — parents recreate it on every render and
    // the resulting re-run would wipe the user's repeat/selected picks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trainerId]);

  if (!open || !action) return null;

  const meta = ACTION_META[action];
  const Icon = meta?.icon || Compass;

  const submit = async (routeId) => {
    setBusy(true);
    try {
      await api.post(`/trainers/${trainerId}/strategy`, {
        strategy: action,
        target: routeId || undefined,
        repeat,
      });
      const routeLabel = routeId
        ? (routes?.find((r) => r.id === routeId)?.name || routeId)
        : "automatique";
      const repeatSuffix = repeat > 1 ? ` (×${repeat})` : "";
      toast.success(`${meta.label} forcée — ${routeLabel}${repeatSuffix}`);
      onAfterSubmit?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#1A1A1A]/70 flex items-center justify-center p-4 overflow-auto"
      onClick={onClose}
      data-testid="force-action-modal"
    >
      <div
        className="bg-[#F4EFE6] border-2 border-[#1A1A1A] paper-noise w-full max-w-2xl max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-[#2B2B2B] p-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <Icon size={28} className="shrink-0 mt-1 text-[#2E4F32]"/>
            <div>
              <div className="overline">Forcer une action</div>
              <h2 className="font-serif-display italic text-2xl leading-tight">
                {meta.label} — choisir la destination
              </h2>
              <p className="text-[11px] font-mono text-[#4A4A4A] mt-1 leading-snug">{meta.description}</p>
            </div>
          </div>
          <button data-testid="force-modal-close" onClick={onClose} className="btn-ink p-1.5 shrink-0">
            <X size={14}/>
          </button>
        </div>

        <div className="p-4">
          {/* Repeat count selector */}
          <div className="panel border border-[#2B2B2B] p-3 mb-3 flex items-center gap-3 flex-wrap" data-testid="force-repeat-picker">
            <div className="flex-1 min-w-[160px]">
              <div className="overline text-[10px]">Répéter l&apos;action</div>
              <div className="font-mono text-[10px] text-[#4A4A4A] mt-0.5">
                Le dresseur enchaînera {repeat} fois cette action{repeat > 1 ? ` de suite avant de revenir à son comportement automatique.` : `.`}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 5, 10].map((n) => (
                <button
                  key={n}
                  data-testid={`force-repeat-${n}`}
                  onClick={() => setRepeat(n)}
                  disabled={busy}
                  className={`btn-ink text-xs w-9 h-9 justify-center ${repeat === n ? "btn-leaf" : ""}`}
                  title={n === 1 ? "Une seule fois" : `Enchaîner ${n} fois`}
                >
                  ×{n}
                </button>
              ))}
            </div>
          </div>

          {/* Auto choice */}
          <button
            data-testid="force-target-auto"
            disabled={busy}
            onClick={() => submit(null)}
            className="panel w-full text-left p-3 mb-3 hover:bg-[#FCF9F2] flex items-center gap-3 border border-[#2B2B2B]"
          >
            <Sparkles size={18} className="text-[#B87333]"/>
            <div className="flex-1">
              <div className="font-serif-display text-lg leading-none">Choix automatique</div>
              <div className="font-mono text-[10px] text-[#4A4A4A]">Le dresseur décide en fonction de son profil et de son progrès.</div>
            </div>
          </button>

          <div className="overline mb-2">Routes limitrophes accessibles</div>
          {!routes ? (
            <div className="font-mono text-xs text-[#7A7A7A]">Chargement…</div>
          ) : (() => {
            const adjacentRoutes = routes.filter((r) => r.adjacent);
            if (adjacentRoutes.length === 0) {
              return (
                <div className="panel border border-[#2B2B2B] p-3 font-mono text-[11px] text-[#4A4A4A]">
                  Aucune route limitrophe disponible pour ce dresseur.
                </div>
              );
            }
            return (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {adjacentRoutes.map((r) => {
                    const locked = !r.accessible;
                    const isSel = selected === r.id;
                    return (
                      <button
                        key={r.id}
                        data-testid={`force-target-${r.id}`}
                        disabled={locked || busy}
                        onClick={() => { setSelected(r.id); submit(r.id); }}
                        className={`panel text-left p-2 border ${isSel ? "border-[#B87333]" : "border-[#2B2B2B]"} ${locked ? "opacity-50 cursor-not-allowed" : "hover:bg-[#FCF9F2]"}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-serif-display text-base leading-tight truncate">{r.name}</div>
                          {locked && <Lock size={11} className="text-[#7A7A7A]"/>}
                        </div>
                        <div className="font-mono text-[10px] text-[#4A4A4A] mt-1 flex flex-wrap gap-2">
                          <span>Difficulté {r.difficulty} · {DIFFICULTY_LABEL(r.difficulty)}</span>
                          <span>·</span>
                          <span>{r.pokemon_count} Pokémon</span>
                          {r.min_badges > 0 && (<><span>·</span><span>Requiert {r.min_badges} badge(s)</span></>)}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="font-mono text-[10px] text-[#8C877A] italic mt-3 leading-snug">
                  Pour rejoindre une route plus lointaine, ouvre la carte du dresseur et lance un <strong>Déplacement</strong> — le trajet sera fait route par route jusqu&apos;à destination.
                </p>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
